import React, { useRef, useEffect, useState } from 'react';
import { useChat } from 'store/useChatStore';
import MessageList from './message-list';
import Suggestions from './suggestions';
import ChatInput from './chat-input';
import LoadingIndicator from './loadingIndicator';
import DataExplorer from './data-explorer';
import useConversations from 'store/useConversationStore';
import { RootState } from 'redux/store';
import { useAppSelector } from 'redux/hooks';
import { RotatingLogo } from 'components/atoms/Base/Logo/RotatingLogo';

interface ChatContainerProps {
  sidebarCollapsed: boolean;
  homepageInsights?: string | null;
  suggestedQuestions?: string[];
  isLoadingInsights?: boolean;
  isNewChatClicked?: boolean;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ sidebarCollapsed, homepageInsights, suggestedQuestions = [], isLoadingInsights, isNewChatClicked = false }) => {
  const { 
    messages, 
    pendingMessage, 
    sendChatMessage, 
    suggestions,
    suggestedVisualizations,
    sendSuggestion,
    loading
  } = useChat();
  const { closeDataExplorer } = useConversations();
  const { dataExplorer } = useAppSelector((state: RootState) => state.conversations);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isViewSchema, setIsViewSchema] = useState<boolean>(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleVisualizationClick = (type: string, description: string) => {
    if (description.includes(type)) {
      sendChatMessage(`Show me a ${description}`);
    } else {
      sendChatMessage(`Show me a ${description} in a ${type}`);
    }
  };

  const handleCloseDrawer = () => {
    closeDataExplorer();
  };

  useEffect(() => {
    setIsViewSchema(dataExplorer);
  }, [dataExplorer]);

  return (
    <div 
      className={`
        w-full h-screen bg-white flex flex-col overflow-hidden
        md:ml-[260px] md:w-[calc(100%-260px)]
        ${sidebarCollapsed ? 'md:ml-[60px] md:w-[calc(100%-60px)]' : ''}
      `}
    >
      <div className="w-full h-full flex flex-col">
        {messages.length === 0 ? (
          loading ? (
            <div className="flex justify-start z-10 items-center p-3 w-full max-w-[856px] mx-auto">
              <LoadingIndicator />
            </div>
          ) : (
            <>
              <div className="flex-1 items-center justify-center overflow-y-auto [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
                <div className="w-full max-w-[856px] mx-auto px-4 py-4 md:px-8 md:py-8 pb-20 md:pb-8 mb-20 md:mb-0">
                  {isNewChatClicked ? (
                    <div className="md:mt-32 flex flex-col items-center justify-center space-y-5">
                      <div className="flex justify-center">
                        <RotatingLogo
                          size={150}
                          rpm={30}
                        />
                      </div> 

                      <div className="text-center w-auto h-auto mt-4">
                        <h1 className="md:text-display text-[24px] text-gray-900 mb-4">
                          Your{' '}
                          <span className="bg-gradient-to-r from-cyan-500 to-green-500 bg-clip-text text-transparent">
                            AI Analyst
                          </span>
                          , on demand
                        </h1>
                        <p className="font-inter text-[20px] md:text-[24px] leading-[28px] md:leading-[32px] text-tertiary-text">
                          Get instant answers from your data.
                        </p>
                      </div>

                      <div className="w-full max-w-[856px] mx-auto px-4 py-4 md:px-8 md:py-6">
                        <ChatInput
                          onSendMessage={sendChatMessage}
                          isLoading={pendingMessage}
                          disabled={pendingMessage}
                        />
                      </div>
                    </div>
                    
                  ) : (
                    <>
                      {isLoadingInsights ? (
                        <div className="flex justify-center items-center py-8">
                          <LoadingIndicator />
                        </div>
                      ) : homepageInsights ? (
                        <div className="w-full max-w-[900px]">
                          <div 
                            className="homepage-insights-content"
                            dangerouslySetInnerHTML={{ __html: homepageInsights }}
                          />
                          {suggestedQuestions && suggestedQuestions.length > 0 && (
                            <div className="mt-6 space-y-3">
                              {suggestedQuestions.map((question, index) => (
                                <button
                                  key={index}
                                  onClick={() => sendChatMessage(question)}
                                  className="text-left rounded-[12px] md:rounded-full text-body hover:text-gray-900 text-black px-4 py-1.5 report-suggestion-item flex items-center gap-2 bg-[#F5F8FA] p-2 border border-[#F5F8FA] cursor-pointer min-w-[15rem] hover:bg-[#E8F0F5] transition-colors duration-200 leading-snug"
                                >
                                  {question}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
               {
                (!isNewChatClicked && homepageInsights) && ( 
                  <div>
                    <div className="md:hidden fixed bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none z-40" />
                    <div className="flex-shrink-0 fixed bottom-10 left-0 right-0 md:static border-gray-200 z-50">
                    <div className="w-full max-w-[856px] mx-auto px-4 py-4 md:px-8 md:py-6">
                      <ChatInput
                        onSendMessage={sendChatMessage}
                        isLoading={pendingMessage}
                        disabled={pendingMessage}
                      />
                    </div>
                  </div>
                  </div>
                )
               }    

               {/* Bottom fade/blur gradient overlay - mobile only */}
              {/* <div className="md:hidden fixed bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none z-40" />
              
              <div className="flex-shrink-0 fixed bottom-10 left-0 right-0 md:static border-gray-200 z-50">
                <div className="w-full max-w-[856px] mx-auto px-4 py-3 md:px-8 md:py-6">
                  <ChatInput
                    onSendMessage={sendChatMessage}
                    isLoading={pendingMessage}
                    disabled={pendingMessage}
                  />
                </div>
              </div> */}
            </>
          )
        ) : (
          <>
            {/* Scrollable messages with bottom padding for fixed input
            <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
              <div className="w-full max-w-[856px] mx-auto px-4 py-4 md:px-8 md:py-8 pb-32 md:pb-8">
                <MessageList messages={messages} />
                <div ref={messagesEndRef} />

                {(suggestions.length > 0 ||
                  (suggestedVisualizations && suggestedVisualizations.length > 0)) &&
                  !pendingMessage && (
                    <div className="mt-4">
                      <Suggestions
                        suggestions={suggestions}
                        suggestedVisualizations={suggestedVisualizations}
                        onSuggestionClick={sendSuggestion}
                        onVisualizationClick={handleVisualizationClick}
                      />
                    </div>
                  )}
              </div>
            </div> */}
            {/* Scrollable messages with bottom padding for fixed input */}
            <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb:hover]:bg-gray-400 relative">
              <div className="w-full max-w-[856px] mx-auto px-4 py-4 md:px-8 md:py-8 pb-36 md:pb-8">
                <MessageList messages={messages} />
                <div ref={messagesEndRef} />

                {(suggestions.length > 0 ||
                  (suggestedVisualizations && suggestedVisualizations.length > 0)) &&
                  !pendingMessage && (
                    <div className="mt-4">
                      <Suggestions
                        suggestions={suggestions}
                        suggestedVisualizations={suggestedVisualizations}
                        onSuggestionClick={sendSuggestion}
                        onVisualizationClick={handleVisualizationClick}
                      />
                    </div>
                  )}
              </div>
              {/* Bottom fade/blur gradient overlay - mobile only */}
              <div className="md:hidden fixed bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none z-40" />
            </div>

            {/* Fixed chat input at bottom - mobile optimized */}
            <div className="flex-shrink-0 fixed bottom-10 left-0 right-0 md:static border-gray-200 z-50">
              <div className="w-full max-w-[856px] mx-auto px-4 py-3 md:px-8 md:py-6">
                <ChatInput
                  onSendMessage={sendChatMessage}
                  isLoading={pendingMessage}
                  disabled={pendingMessage}
                />
              </div>
            </div>
          </>
        )}

        <DataExplorer isOpen={isViewSchema} onClose={handleCloseDrawer} />
      </div>
    </div>
  );
};

export default ChatContainer;